import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Global Modal Context
 * Manages marketplace modal state globally to enable opening modal on any page
 * without requiring navigation. Follows NavigationContext pattern.
 */
const GlobalModalContext = createContext(null);

// Image upload constants
const MODAL_CONSTANTS = {
  MAX_FILES: 10,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  MAX_IMAGE_DIMENSION: 1920,
  COMPRESSION_QUALITY: 0.85
};

/**
 * Global Modal Provider - wrap your app with this component
 */
export const GlobalModalProvider = ({ children }) => {
  // Marketplace Modal State
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);
  const [showMarketplaceSuccess, setShowMarketplaceSuccess] = useState(false);

  // Form fields
  const [marketplaceType, setMarketplaceType] = useState('offer');
  const [marketplaceTitle, setMarketplaceTitle] = useState('');
  const [marketplaceContent, setMarketplaceContent] = useState('');
  const [marketplacePrice, setMarketplacePrice] = useState('');
  // Issue #83: Free giveaway checkbox
  const [marketplaceIsFreeGiveaway, setMarketplaceIsFreeGiveaway] = useState(false);

  // Images
  const [marketplaceImages, setMarketplaceImages] = useState([]);
  const [marketplaceImagePreviews, setMarketplaceImagePreviews] = useState([]);

  // Loading state
  const [isCreatingMarketplace, setIsCreatingMarketplace] = useState(false);

  // Callback for success (for refreshing page-specific data)
  const [onSuccessCallback, setOnSuccessCallback] = useState(null);

  // Reset form state
  const resetMarketplaceModal = useCallback(() => {
    setMarketplaceType('offer');
    setMarketplaceTitle('');
    setMarketplaceContent('');
    setMarketplacePrice('');
    setMarketplaceIsFreeGiveaway(false); // Issue #83
    setMarketplaceImages([]);
    setMarketplaceImagePreviews([]);
    setIsCreatingMarketplace(false);
  }, []);

  // Open the marketplace modal
  const openMarketplaceModal = useCallback((options = {}) => {
    const { type = 'offer', onSuccess = null } = options;
    resetMarketplaceModal();
    setMarketplaceType(type);
    if (onSuccess) {
      setOnSuccessCallback(() => onSuccess);
    }
    setShowMarketplaceModal(true);
  }, [resetMarketplaceModal]);

  // Close and reset the marketplace modal
  const closeMarketplaceModal = useCallback(() => {
    setShowMarketplaceModal(false);
    resetMarketplaceModal();
    setOnSuccessCallback(null);
  }, [resetMarketplaceModal]);

  // Show success message and call callback
  // Issue #216: Pass marketplaceType to callback so filter can be updated
  const showSuccess = useCallback(() => {
    setShowMarketplaceModal(false);
    setShowMarketplaceSuccess(true);

    // Call success callback if provided, passing the created marketplace type
    if (onSuccessCallback) {
      onSuccessCallback(marketplaceType);
      setOnSuccessCallback(null);
    }

    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowMarketplaceSuccess(false);
      resetMarketplaceModal();
    }, 3000);
  }, [onSuccessCallback, resetMarketplaceModal, marketplaceType]);

  const value = {
    // State
    showMarketplaceModal,
    showMarketplaceSuccess,
    marketplaceType,
    marketplaceTitle,
    marketplaceContent,
    marketplacePrice,
    marketplaceIsFreeGiveaway, // Issue #83
    marketplaceImages,
    marketplaceImagePreviews,
    isCreatingMarketplace,

    // Setters
    setMarketplaceType,
    setMarketplaceTitle,
    setMarketplaceContent,
    setMarketplacePrice,
    setMarketplaceIsFreeGiveaway, // Issue #83
    setMarketplaceImages,
    setMarketplaceImagePreviews,
    setIsCreatingMarketplace,

    // Actions
    openMarketplaceModal,
    closeMarketplaceModal,
    resetMarketplaceModal,
    showSuccess,

    // Constants
    ...MODAL_CONSTANTS
  };

  return (
    <GlobalModalContext.Provider value={value}>
      {children}
    </GlobalModalContext.Provider>
  );
};

/**
 * Custom hook to access global modal functions
 * Usage: const { openMarketplaceModal, closeMarketplaceModal } = useGlobalModal();
 */
export const useGlobalModal = () => {
  const context = useContext(GlobalModalContext);
  if (!context) {
    throw new Error('useGlobalModal must be used within a GlobalModalProvider');
  }
  return context;
};

export default GlobalModalContext;
